import { memo, useCallback } from 'react'

/**
 * EntityDetailTabs - Underline-indicator tab navigation for entity detail panel.
 * Uses ARIA tablist/tab roles. Not styled as action buttons.
 * @param {{ activeTab: string, onTabChange: (id: string) => void, tabs: Array<{id: string, label: string}> }} props
 * @atomic @idempotent
 */
const EntityDetailTabs = memo(function EntityDetailTabs({ activeTab, onTabChange, tabs }) {
  const handleClick = useCallback((id) => () => onTabChange(id), [onTabChange])
  const handleTouch = useCallback(
    (id) => (e) => { e.preventDefault(); onTabChange(id) },
    [onTabChange]
  )

  return (
    <div
      role="tablist"
      className="flex border-b border-gray-200 mb-1 mt-2"
    >
      {tabs.map(tab => {
        const isActive = activeTab === tab.id
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={handleClick(tab.id)}
            onTouchEnd={handleTouch(tab.id)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onTabChange(tab.id)}
            className={`px-4 py-2.5 text-xs font-semibold cursor-pointer select-none transition-colors relative ${
              isActive
                ? 'text-primary'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {tab.label}
            {isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
            )}
          </div>
        )
      })}
    </div>
  )
})

export default EntityDetailTabs
